

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Badge } from "@/components/ui/badge"
import { CheckCircle, AlertCircle, FileText, Zap, Brain, Database } from "lucide-react"

interface DocumentUploadProgressProps {
  filename: string
  stage: 'uploading' | 'processing' | 'embedding' | 'completed' | 'error'
  progress: number
  error?: string
  processingResult?: Record<string, unknown>
}

export function DocumentUploadProgress({ 
  filename, 
  stage, 
  progress, 
  error, 
  processingResult 
}: DocumentUploadProgressProps) {
  const getStageInfo = (currentStage: string) => {
    const stages = {
      uploading: { 
        label: 'Uploading File', 
        icon: FileText, 
        color: 'bg-primary',
        description: 'Uploading document to server...' 
      },
      processing: { 
        label: 'Docling Processing', 
        icon: Zap, 
        color: 'bg-warning',
        description: 'Processing with advanced Docling pipeline...' 
      },
      embedding: { 
        label: 'Creating Embeddings', 
        icon: Brain, 
        color: 'bg-primary',
        description: 'Generating AI embeddings for search...' 
      },
      completed: { 
        label: 'Upload Complete', 
        icon: CheckCircle, 
        color: 'bg-success',
        description: 'Document uploaded successfully! Processing continues in background.' 
      },
      error: { 
        label: 'Processing Error', 
        icon: AlertCircle, 
        color: 'bg-destructive',
        description: error || 'An error occurred during processing' 
      }
    }
    return stages[currentStage as keyof typeof stages]
  }

  const stageInfo = getStageInfo(stage)
  const Icon = stageInfo.icon

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Icon className={`h-4 w-4 ${stage === 'completed' ? 'text-success' : stage === 'error' ? 'text-destructive' : 'text-primary'}`} />
          Processing: {filename}
          <Badge variant={stage === 'completed' ? 'default' : stage === 'error' ? 'destructive' : 'secondary'}>
            {stageInfo.label}
          </Badge>
        </CardTitle>
        <CardDescription className="text-xs">
          {stageInfo.description}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-2">
        {/* Progress Bar */}
        <div className="space-y-2">
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{Math.round(progress)}% Complete</span>
            <span>{stageInfo.label}</span>
          </div>
        </div>

        {/* Stage Indicators */}
        <div className="flex items-center justify-between">
          {['uploading', 'processing', 'embedding', 'completed'].map((stageKey, index) => {
            const stageConfig = getStageInfo(stageKey)
            const StageIcon = stageConfig.icon
            // const isActive = stage === stageKey
            const isCompleted = ['uploading', 'processing', 'embedding', 'completed'].indexOf(stage) > index
            const isCurrent = stage === stageKey
            
            return (
              <div key={stageKey} className="flex flex-col items-center gap-1">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center transition-colors
                  ${isCompleted || isCurrent ? 'bg-primary/10 dark:bg-primary/20' : 'bg-muted'}
                `}>
                  <StageIcon className={`h-4 w-4 ${
                    isCompleted ? 'text-success' : 
                    isCurrent ? 'text-primary' : 
                    'text-muted-foreground'
                  }`} />
                </div>
                <span className={`text-xs ${
                  isCompleted || isCurrent ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {stageConfig.label.split(' ')[0]}
                </span>
              </div>
            )
          })}
        </div>

        {/* Processing Results */}
        {stage === 'completed' && processingResult && (
          <div className="mt-4 rounded-lg border border-success bg-success p-3/20">
            <div className="mb-2 flex items-center gap-2">
              <Database className="h-4 w-4 text-success" />
              <span className="text-sm font-medium text-success">
                {processingResult.background_processing ? 'Upload Complete - Processing in Background' : 'Docling Processing Complete'}
              </span>
            </div>
            {processingResult.background_processing ? (
              <div className="text-xs text-success">
                <p>{typeof processingResult.message === 'string' ? processingResult.message : 'Processing...'}</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-muted-foreground">Document Type:</span>
                    <span className="ml-2 font-medium">{typeof processingResult.document_type === 'string' ? processingResult.document_type : 'auto'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Service:</span>
                    <span className="ml-2 font-medium">MasterRAGService</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Chunks Created:</span>
                  <span className="ml-2 font-medium">{typeof processingResult.chunks_created === 'number' ? processingResult.chunks_created : 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Processing Time:</span>
                  <span className="ml-2 font-medium">{typeof processingResult.processing_time_ms === 'number' ? processingResult.processing_time_ms : 0}ms</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Document Type:</span>
                  <span className="ml-2 font-medium">{typeof processingResult.document_type === 'string' ? processingResult.document_type : 'auto'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Service:</span>
                  <span className="ml-2 font-medium">MasterRAGService</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error Details */}
        {stage === 'error' && error && (
          <div className="mt-4 rounded-lg border border-destructive bg-destructive p-3/20">
            <div className="mb-2 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="text-sm font-medium text-destructive">
                Processing Failed
              </span>
            </div>
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
